import { useCallback } from 'react';
import useLocalStorage from './useLocalStorage';

export default function usePushNotificationPermission() {
  const [pushPermissionState, setPushPermissionState] = useLocalStorage(
    'pushNotificationPermission',
    {
      requested: false,
      requestedDate: null,
      maybeLater: false,
      maybeLaterDate: null,
      dismissCount: 0,
      nextPromptDate: null,
    }
  );

  const hasRequestedPermission = useCallback(
    () => pushPermissionState.requested,
    [pushPermissionState.requested]
  );

  const hasDismissedPermission = useCallback(() => {
    // Check if user dismissed and if enough time has passed to show again
    if (!pushPermissionState.maybeLater) return false;

    if (!pushPermissionState.nextPromptDate) return false;

    const now = new Date();
    const nextPrompt = new Date(pushPermissionState.nextPromptDate);

    // If enough time has passed, allow showing again
    return now < nextPrompt;
  }, [pushPermissionState.maybeLater, pushPermissionState.nextPromptDate]);

  const markAsRequested = () => {
    setPushPermissionState({
      requested: true,
      requestedDate: new Date().toISOString(),
      maybeLater: false,
      maybeLaterDate: null,
    });
  };

  const markAsMaybeLater = () => {
    const dismissCount = (pushPermissionState.dismissCount || 0) + 1;

    // Check if test mode is enabled
    const isTestMode =
      process.env.REACT_APP_PUSH_NOTIFICATION_TEST_MODE === 'true';

    // Progressive delay based on mode
    const delays = isTestMode
      ? [
          30 * 1000, // 30 seconds (test)
          2 * 60 * 1000, // 2 minutes (test)
          5 * 60 * 1000, // 5 minutes (test)
          10 * 60 * 1000, // 10 minutes (test)
          20 * 60 * 1000, // 20 minutes (test max)
        ]
      : [
          3 * 60 * 60 * 1000, // 3 hours (prod)
          24 * 60 * 60 * 1000, // 1 day (prod)
          3 * 24 * 60 * 60 * 1000, // 3 days (prod)
          7 * 24 * 60 * 60 * 1000, // 1 week (prod)
          14 * 24 * 60 * 60 * 1000, // 2 weeks (prod max)
        ];

    const delayIndex = Math.min(dismissCount - 1, delays.length - 1);
    const delay = delays[delayIndex];

    if (isTestMode) {
      console.log(
        `[Push Notification Test Mode] Dismiss #${dismissCount}, next prompt in: ${delay / 1000}s`
      );
    }

    const nextPromptDate = new Date(Date.now() + delay).toISOString();

    setPushPermissionState({
      ...pushPermissionState,
      maybeLater: true,
      maybeLaterDate: new Date().toISOString(),
      dismissCount,
      nextPromptDate,
    });
  };

  const resetPermissionState = () => {
    setPushPermissionState({
      requested: false,
      requestedDate: null,
      maybeLater: false,
      maybeLaterDate: null,
    });
  };

  return {
    hasRequestedPermission,
    hasDismissedPermission,
    markAsRequested,
    markAsMaybeLater,
    resetPermissionState,
  };
}
